import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router';
import { useOwnProfile } from '@/entities/profile';
import { useSession } from '@/entities/session';
import { useOwnStaffRoles } from '@/entities/staff';
import { PendingInvitationsBanner } from '@/features/member/accept-invitation';
import { designNavItem, SignedInShell, staffAreaNavItem } from '@/widgets/app-shell';
import { OrgScopeProvider } from './org-scope';
import { useClearCacheOnUserChange } from './use-clear-cache-on-user-change';

/**
 * Gate and frame for signed-in routes: redirects when signed out, otherwise loads the org scope and wraps
 * the app shell around the page.
 */
export function RequireAuth() {
  return (
    <SignedInFrame>
      <Outlet />
    </SignedInFrame>
  );
}

/** The signed-in customer frame around `children`. `AdminGate` uses it to show not-found to non-staff. */
export function SignedInFrame({ children }: { children: ReactNode }) {
  const { t } = useTranslation('shell');
  const { session, loading } = useSession();
  useClearCacheOnUserChange(loading ? null : (session?.user.id ?? null));
  const profile = useOwnProfile(session?.user.id);
  // empty for every customer; staff get a link into /admin (the gate and SQL decide the rest)
  const staffRoles = useOwnStaffRoles(session?.user.id);
  if (loading) return null;
  if (!session) return <Navigate to="/sign-in" replace />;
  return (
    <OrgScopeProvider userId={session.user.id}>
      <SignedInShell
        userEmail={session.user.email ?? ''}
        {...(profile.data
          ? { userName: { firstName: profile.data.first_name, lastName: profile.data.last_name } }
          : {})}
        // A build-time constant: the production bundle has no trace of /design.
        extraBottomItems={[
          ...(staffRoles.data && staffRoles.data.length > 0
            ? [staffAreaNavItem(t('nav.staffArea'))]
            : []),
          ...(import.meta.env.DEV ? [designNavItem(t('nav.design'))] : []),
        ]}
      >
        <PendingInvitationsBanner />
        {children}
      </SignedInShell>
    </OrgScopeProvider>
  );
}
